/**
 * Internationalization support for Hatched widgets.
 * Provides English translations.
 */

export interface Translations {
  // Common
  loading: string;
  error: string;
  coins: string;
  noData: string;

  // Buddy
  skills: string;
  /** Skill Radar (buddy_skills_radar) — view-toggle labels. */
  skillViewBars: string;
  skillViewRadar: string;
  skillViewToggleLabel: string;
  badges: string;
  evolution: string;
  evolutionTimeline: string;
  evolutionEmpty: string;
  equipped: string;
  stageEgg: string;
  stageBaby: string;
  stageChild: string;
  stageTeen: string;
  stageAdult: string;
  stageMaster: string;
  leaderboardRank: string;
  preview: string;
  processing: string;

  // Leaderboard
  leaderboard_title: string;
  leaderboard_weekly: string;
  leaderboard_monthly: string;
  leaderboard_all_time: string;
  leaderboard_xp: string;
  leaderboard_coins: string;
  leaderboard_badges: string;
  leaderboard_evolution: string;
  leaderboard_participants: string;
  leaderboard_your_rank: string;
  leaderboard_no_entries: string;
  leaderboard_loading: string;

  // Tokens
  tokens_title: string;
  tokens_progression: string;
  tokens_uncapped: string;

  // Marketplace
  marketplace: string;
  buy: string;
  equip: string;
  unequip: string;
  owned: string;
  confirmPurchase: string;
  purchaseSuccess: string;
  purchaseFailed: string;
  equipSuccess: string;
  equipFailed: string;
  unequipSuccess: string;
  unequipFailed: string;
  confirmEquip: string;
  confirmUnequip: string;
  locked: string;
  noItems: string;
  all: string;
  cancel: string;
  confirm: string;
  buying: string;

  // Marketplace gifting (HTCH-53 — Social Treasure)
  mkt_buy_for_self?: string;
  mkt_gift_to_teammate?: string;
  mkt_gift_only_badge?: string;
  mkt_gift_modal_title?: string;
  mkt_gift_recipient_label?: string;
  mkt_gift_recipient_placeholder?: string;
  mkt_gift_message_label?: string;
  mkt_gift_message_placeholder?: string;
  mkt_gift_price_preview?: string;
  mkt_gift_confirm?: string;
  mkt_gift_sending?: string;
  mkt_gift_sent_toast?: string;
  mkt_gift_duplicate_toast?: string;
  mkt_gift_failed?: string;
  mkt_gift_no_teammates?: string;
  mkt_gift_pick_recipient?: string;
  mkt_gift_insufficient?: string;

  // Hatch ceremony
  hatch_intro_title?: string;
  hatch_ready_title?: string;
  hatch_tap_hint?: string;
  hatch_cta?: string;
  hatch_skip?: string;
  hatch_readonly_hint?: string;
  hatch_revealed_eyebrow?: string;
  hatch_revealed_title?: string;
  hatch_revealed_sub?: string;
  hatch_continue?: string;
  hatch_name_eyebrow?: string;
  hatch_name_title?: string;
  hatch_name_hint?: string;
  hatch_confirm?: string;
  hatch_keep_default?: string;
  hatch_welcome_eyebrow?: string;
  hatch_welcome_suffix?: string;

  // Leaderboard context
  lb_percentile?: string;
  lb_gap_to_next?: string;
  lb_top?: string;
  lb_points_away?: string;
  lb_tied?: string;

  // Marketplace delight
  mkt_insufficient?: string;
  mkt_need_more?: string;
  mkt_affordable?: string;

  // Read-only demo hint shown on the preview panel when the widget
  // runs with an embed token (no marketplace:purchase scope).
  mkt_demo_title?: string;
  mkt_demo_body?: string;
  mkt_signin_hint?: string;
  mkt_scope_banner_title?: string;
  mkt_scope_banner_body?: string;
  mkt_purchase_requires_session?: string;
  mkt_equip_requires_session?: string;
  mkt_confirm_equip_title?: string;
  mkt_confirm_unequip_title?: string;
  mkt_confirm_appearance_note?: string;
  mkt_appearance_queued?: string;
  mkt_appearance_pending?: string;
  mkt_appearance_pending_short?: string;
  mkt_appearance_waiting_credits?: string;
  mkt_appearance_failed?: string;

  // HTCH-86 — F3.9 Marketplace FOMO copy. Truthful scarcity only.
  mkt_fomo_viewing?: string;
  mkt_fomo_low_stock?: string;
  mkt_flash_sale_off?: string;
  mkt_flash_sale_ends_in?: string;

  // Dress mode (HTCH-8). Fall back to English copy at the call site when a
  // locale doesn't supply these keys.
  dress_tab_shop?: string;
  dress_tab_dress?: string;
  dress_save_cta?: string;
  dress_save_title?: string;
  dress_save_body?: string;
  dress_name_placeholder?: string;
  dress_name_required?: string;
  dress_pick_one?: string;
  dress_cap_reached?: string;
  dress_saved?: string;
  dress_deleted?: string;
  dress_activated?: string;
  dress_wear?: string;
  dress_wearing?: string;
  dress_none?: string;
  dress_strip_empty?: string;
  dress_slot_empty?: string;
  dress_composing?: string;
  dress_save_btn?: string;
  delete?: string;
  saving?: string;
  // F1.13 Build From Scratch — first-look payoff after the first outfit is saved.
  first_look_done_title?: string;
  first_look_done_body?: string;

  // Leaderboard view mode (HTCH-18)
  lb_view_mode_label?: string;
  lb_view_top?: string;
  lb_view_around_me?: string;
  lb_view_hybrid?: string;
  lb_top_strip_label?: string;
  lb_neighbors_label?: string;

  // Leaderboard scope (HTCH-21)
  lb_scope_label?: string;
  lb_scope_global?: string;
  lb_scope_locked?: string;

  // Leaderboard team scope (HTCH-49)
  lb_scope_team?: string;
  lb_scope_team_disabled?: string;
  lb_team_empty_title?: string;
  lb_team_empty_sub?: string;

  // Kudos widget (HTCH-52)
  kudos_title?: string;
  kudos_send_panel?: string;
  kudos_received_panel?: string;
  kudos_given_panel?: string;
  kudos_recipient_label?: string;
  kudos_recipient_placeholder?: string;
  kudos_type_label?: string;
  kudos_message_label?: string;
  kudos_message_label_required?: string;
  kudos_message_placeholder?: string;
  kudos_send_cta?: string;
  kudos_sending?: string;
  kudos_sent_toast?: string;
  kudos_duplicate_toast?: string;
  kudos_cap_hit?: string;
  kudos_cap_countdown?: string;
  kudos_no_teammates?: string;
  kudos_received_empty?: string;
  kudos_given_empty?: string;
  kudos_given_count?: string;
  kudos_pick_recipient?: string;
  kudos_pick_type?: string;
  kudos_loading?: string;
  kudos_error?: string;

  // SeeSaw Bump feed widget (HTCH-63)
  feed_title?: string;
  feed_empty?: string;
  feed_show_more?: string;

  // Glowing Choice next-step strip (F1.12)
  next_step?: string;

  // Badge collection grid (HTCH-16)
  badge_filter_all?: string;
  badge_filter_earned?: string;
  badge_filter_locked?: string;
  badge_counter_label?: string;
  badge_how_to_earn?: string;
  badge_earned_on?: string;
  badge_no_badges_yet?: string;
  badge_no_locked?: string;
  badge_no_earned_yet?: string;

  // Marketplace countdown (HTCH-15)
  countdown_drops_in?: string;
  countdown_ends_in?: string;
  countdown_sold_out?: string;
  countdown_days?: string;
  countdown_hours?: string;
  countdown_minutes?: string;
  countdown_seconds?: string;
  countdown_available_for?: string;
}

const en: Translations = {
  loading: 'Loading...',
  error: 'Something went wrong',
  coins: 'coins',
  noData: 'No data available',

  skills: 'Skills',
  skillViewBars: 'Bars',
  skillViewRadar: 'Radar',
  skillViewToggleLabel: 'Skill view',
  badges: 'Badges',
  evolution: 'Evolution',
  evolutionTimeline: 'Evolution timeline',
  evolutionEmpty: 'No stage transitions yet',
  equipped: 'Equipped',
  stageEgg: 'Egg',
  stageBaby: 'Baby',
  stageChild: 'Child',
  stageTeen: 'Teen',
  stageAdult: 'Adult',
  stageMaster: 'Master',
  leaderboardRank: 'Rank',
  preview: 'Preview',
  processing: 'Processing...',

  leaderboard_title: 'Leaderboard',
  leaderboard_weekly: 'Weekly',
  leaderboard_monthly: 'Monthly',
  leaderboard_all_time: 'All Time',
  leaderboard_xp: 'XP',
  leaderboard_coins: 'Coins',
  leaderboard_badges: 'Badges',
  leaderboard_evolution: 'Evolution',
  leaderboard_participants: 'participants',
  leaderboard_your_rank: 'Your rank',
  leaderboard_no_entries: 'No entries yet',
  leaderboard_loading: 'Loading leaderboard...',

  tokens_title: 'Wallet',
  tokens_progression: 'Progression',
  tokens_uncapped: 'No cap',

  marketplace: 'Marketplace',
  buy: 'Buy',
  equip: 'Equip',
  unequip: 'Unequip',
  owned: 'Owned',
  confirmPurchase: 'Buy {item} for {price} coins?',
  purchaseSuccess: 'Purchase successful!',
  purchaseFailed: 'Purchase failed',
  equipSuccess: 'Item equipped!',
  equipFailed: 'Failed to equip item',
  unequipSuccess: 'Item unequipped!',
  unequipFailed: 'Failed to unequip item',
  confirmEquip:
    'Equip {item}? This changes your buddy image and may start AI generation if this exact look is not cached yet.',
  confirmUnequip:
    'Remove {item}? This changes your buddy image and may start AI generation if this exact look is not cached yet.',
  locked: 'Locked',
  noItems: 'No items available',
  all: 'All',
  cancel: 'Cancel',
  confirm: 'Confirm',
  buying: 'Buying...',

  mkt_buy_for_self: 'Buy for yourself',
  mkt_gift_to_teammate: '🎁 Gift to teammate',
  mkt_gift_only_badge: 'Gift only',
  mkt_gift_modal_title: 'Gift {item}',
  mkt_gift_recipient_label: 'To',
  mkt_gift_recipient_placeholder: 'Search teammates…',
  mkt_gift_message_label: 'Add a note (optional)',
  mkt_gift_message_placeholder: 'Say something nice…',
  mkt_gift_price_preview: 'This gift costs you {price} coins.',
  mkt_gift_confirm: 'Send gift',
  mkt_gift_sending: 'Sending…',
  mkt_gift_sent_toast: 'Gift sent!',
  mkt_gift_duplicate_toast: 'Already sent — thanks for the enthusiasm!',
  mkt_gift_failed: 'Could not send the gift',
  mkt_gift_no_teammates: 'No teammates to gift to yet.',
  mkt_gift_pick_recipient: 'Pick a teammate',
  mkt_gift_insufficient: 'You need {n} more coins for this gift.',

  hatch_intro_title: 'Something is waking',
  hatch_ready_title: 'Your companion stirs',
  hatch_tap_hint: 'Tap the egg three times to help them out',
  hatch_cta: 'Help them hatch',
  hatch_skip: 'Skip ceremony',
  hatch_readonly_hint: 'Your buddy is almost here…',
  hatch_revealed_eyebrow: 'Newly hatched',
  hatch_revealed_title: 'Meet your companion',
  hatch_revealed_sub: 'Freshly arrived, just for you.',
  hatch_continue: 'Name them',
  hatch_name_eyebrow: 'Name your buddy',
  hatch_name_title: 'What shall we call them?',
  hatch_name_hint: 'You can always change it later from your dashboard.',
  hatch_confirm: 'Say hello',
  hatch_keep_default: 'Keep the suggested name',
  hatch_welcome_eyebrow: 'Welcome',
  hatch_welcome_suffix: 'has arrived',

  lb_percentile: 'Top {pct}%',
  lb_gap_to_next: '{n} to next rank',
  lb_top: 'Leading the pack',
  lb_points_away: '{n} behind #{rank}',
  lb_tied: 'Tied at #{rank}',

  mkt_insufficient: 'Need {n} more coins',
  mkt_need_more: 'Short {n}',
  mkt_affordable: 'Affordable',

  mkt_demo_title: 'Read-only preview',
  mkt_demo_body:
    'In a live integration your player spends coins and the creature equips the item instantly — stages, rarities, and lock rules stay respected.',
  mkt_signin_hint: 'Sign in to purchase',
  mkt_scope_banner_title: 'Read-only mount',
  mkt_scope_banner_body:
    'This widget was loaded with an embed token. Purchases and item changes need a session token with marketplace scopes. Issue one server-side and pass it via data-session-token.',
  mkt_purchase_requires_session: 'Purchase needs a session token. Embed tokens are read-only.',
  mkt_equip_requires_session: 'Equipping items needs a session token. Embed tokens are read-only.',
  mkt_confirm_equip_title: 'Update appearance',
  mkt_confirm_unequip_title: 'Update appearance',
  mkt_confirm_appearance_note:
    'While generation runs, your current image stays visible and new item changes are paused.',
  mkt_appearance_queued: 'Appearance update started.',
  mkt_appearance_pending: 'Appearance update is being generated.',
  mkt_appearance_pending_short: 'Updating',
  mkt_appearance_waiting_credits: 'Appearance update is waiting for AI credits.',
  mkt_appearance_failed: 'Appearance update failed. You can try another change.',

  mkt_fomo_viewing: '{n} people viewing',
  mkt_fomo_low_stock: 'Last {n} in stock',
  mkt_flash_sale_off: '{n}% off',
  mkt_flash_sale_ends_in: 'Ends in {time}',

  countdown_drops_in: 'Drops in {duration}',
  countdown_ends_in: 'Ends in {duration}',
  countdown_sold_out: 'Sold out',
  countdown_days: '{n}d',
  countdown_hours: '{n}h',
  countdown_minutes: '{n}m',
  countdown_seconds: '{n}s',
  countdown_available_for: 'Available for {duration}',

  lb_view_mode_label: 'View',
  lb_view_top: 'Top',
  lb_view_around_me: 'Near me',
  lb_view_hybrid: 'Mixed',
  lb_top_strip_label: 'Leaders',
  lb_neighbors_label: 'Your zone',
  lb_scope_label: 'Scope',
  lb_scope_global: 'Everyone',
  lb_scope_locked: 'Your admin has locked the scope.',
  lb_scope_team: 'Team',
  lb_scope_team_disabled: 'Teams not enabled for this workspace.',
  lb_team_empty_title: 'Join a team to see your team leaderboard',
  lb_team_empty_sub: 'Once you are on a team, you will see how you rank alongside your teammates here.',

  kudos_title: 'Recognition',
  kudos_send_panel: 'Send a kudos',
  kudos_received_panel: 'Received',
  kudos_given_panel: 'Given',
  kudos_recipient_label: 'To',
  kudos_recipient_placeholder: 'Search teammates…',
  kudos_type_label: 'Recognition',
  kudos_message_label: 'Add a note (optional)',
  kudos_message_label_required: 'Add a note',
  kudos_message_placeholder: 'What did they do well?',
  kudos_send_cta: 'Send kudos',
  kudos_sending: 'Sending…',
  kudos_sent_toast: 'Kudos sent!',
  kudos_duplicate_toast: 'Already sent — thanks for the enthusiasm!',
  kudos_cap_hit: "You've sent today's {cap} kudos. The next batch unlocks at midnight UTC.",
  kudos_cap_countdown: 'Unlocks in {time}',
  kudos_no_teammates: 'No teammates to recognize yet.',
  kudos_received_empty: 'Your first kudos is on its way. Until then, send one to a teammate.',
  kudos_given_empty: 'Be the first to recognize someone today.',
  kudos_given_count: "You've given {count} kudos",
  kudos_pick_recipient: 'Pick a teammate',
  kudos_pick_type: 'Pick a recognition',
  kudos_loading: 'Loading recognition…',
  kudos_error: 'Could not load recognition.',

  // SeeSaw Bump feed widget (HTCH-63)
  feed_title: 'Team feed',
  feed_empty:
    'Quiet day on the team — celebrate your first win to bump the feed.',
  feed_show_more: 'Show more',

  next_step: 'Next step',

  first_look_done_title: 'First look saved!',
  first_look_done_body: "{name} is dressed — you've earned the First Look badge.",

  badge_filter_all: 'All',
  badge_filter_earned: 'Earned',
  badge_filter_locked: 'To earn',
  badge_counter_label: '{earned} / {total}',
  badge_how_to_earn: 'How to earn',
  badge_earned_on: 'Earned {date}',
  badge_no_badges_yet: 'No badges configured yet',
  badge_no_locked: 'You earned them all — incredible work.',
  badge_no_earned_yet: 'No badges earned yet. Start your collection.',
};

/** Locales the widget loader ships translations for. */
export const SUPPORTED_LOCALES = ['en'] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

/** Public-facing list of locales — exposed via `window.HatchedWidgets.listLocales()`. */
export function listSupportedLocales(): readonly SupportedLocale[] {
  return SUPPORTED_LOCALES;
}

/**
 * Get translations for a given language code.
 * Always returns English.
 */
export function getTranslations(_lang?: string): Translations {
  return en;
}

/**
 * Get stage label in the given language.
 */
export function getStageLabel(stage: number, t: Translations): string {
  const labels: Record<number, string> = {
    0: t.stageEgg,
    1: t.stageEgg,
    2: t.stageBaby,
    3: t.stageTeen,
    4: t.stageAdult,
    5: t.stageMaster,
  };
  return labels[stage] ?? `Stage ${stage}`;
}
